import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-toastify';
import { AlertTriangle, X, Info, CheckCircle, InformationCircle } from 'lucide-react';
import { doc, addDoc, collection, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { CANCEL_REASONS, CANCELLATION_STATUS, ORDER_STATUS } from '../../types/cancellation';

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, buyerId: string, sellerId: string) => Promise<void>;
  isSeller: boolean;
  loading: boolean;
  orderTotal: number;
  orderSubtotal: number;
  shippingCost: number;
  buyerId?: string;
  sellerId?: string;
  isOnlineSale?: boolean;
  isPrivateSale?: boolean;
  isManualOrder?: boolean;
  order: any; // Dados completos do pedido
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSeller,
  loading,
  orderTotal,
  orderSubtotal,
  shippingCost,
  buyerId,
  sellerId,
  isOnlineSale,
  isPrivateSale,
  isManualOrder = false,
  order
}) => {
  const { currentUser } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'reason' | 'notes' | 'confirmation'>('reason');

  const getCancellationWarning = (reason: string) => {
    if (!reason) return null;

    switch (reason) {
      case CANCEL_REASONS.BUYER_REQUEST:
        return {
          title: 'Cancelamento solicitado pelo comprador',
          message: 'O comprador será reembolsado do valor total (incluindo portes). Não haverá penalização para o vendedor.',
          type: 'info'
        };
      case CANCEL_REASONS.OUT_OF_STOCK:
        return {
          title: 'Falta de stock',
          message: 'O comprador será reembolsado do valor total (incluindo portes). ATENÇÃO: Será aplicada uma penalização ao vendedor por cancelamento devido à falta de stock.',
          type: 'warning'
        };
      case CANCEL_REASONS.SELLER_CANCEL:
        return {
          title: 'Cancelamento solicitado pelo vendedor',
          message: 'O comprador será reembolsado do valor total (incluindo portes). ATENÇÃO: Será aplicada uma penalização ao vendedor por cancelamento voluntário.',
          type: 'warning'
        };
      default:
        return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.uid) return;

    if (!selectedReason) {
      setError('Por favor, selecione um motivo para o cancelamento');
      return;
    }

    if (!notes.trim()) {
      setError('Por favor, forneça detalhes adicionais sobre o cancelamento');
      return;
    }

    try {
      // 1. Criar solicitação de cancelamento
      await addDoc(collection(db, 'cancellation_requests'), {
        orderId: order.id,
        orderNumber: order.orderNumber || order.id,
        orderData: order,
        requestDate: new Date().toISOString(),
        requestedBy: currentUser.uid,
        reason: selectedReason,
        additionalNotes: notes,
        status: CANCELLATION_STATUS.PENDING,
        companyId: order.companyId || currentUser.companyId,
        source: isOnlineSale ? 'marketplace' : isPrivateSale ? 'private' : isManualOrder ? 'manual' : 'standard',
        orderType: isOnlineSale ? 'marketplace' : isPrivateSale ? 'private' : 'standard'
      });

      // 2. Atualizar a ordem com a flag de cancelamento pendente
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, {
        hasCancellationRequest: true,
        lastUpdated: new Date().toISOString()
      });

      toast.success('Solicitação de cancelamento enviada com sucesso! Aguarde a análise do suporte.');
      onClose();
    } catch (error: any) {
      console.error('Error submitting cancellation request:', error);
      toast.error('Erro ao enviar solicitação de cancelamento');
    }
  };

  const getRefundDetails = (reason: string) => {
    const isBuyerRequested = reason === CANCEL_REASONS.BUYER_REQUEST;
    const isOutOfStock = reason === CANCEL_REASONS.OUT_OF_STOCK;
    const isSellerCancel = reason === CANCEL_REASONS.SELLER_CANCEL;
    
    return {
      buyerRefund: orderTotal || 0,
      sellerDebit: orderSubtotal || 0,
      shipping: shippingCost || 0,
      isBuyerRequested,
      isOutOfStock,
      isSellerCancel
    };
  };

  const warning = getCancellationWarning(selectedReason);

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Solicitar Cancelamento
            </Dialog.Title>
            <p className="mt-2 text-sm text-gray-500">
              Por favor, selecione o motivo do cancelamento e forneça informações adicionais.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo do Cancelamento
                </label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Selecione um motivo</option>
                  <option value={CANCEL_REASONS.BUYER_REQUEST}>Solicitação do comprador</option>
                  <option value={CANCEL_REASONS.OUT_OF_STOCK}>Falta de stock</option>
                  <option value={CANCEL_REASONS.SELLER_CANCEL}>Vendedor solicitou cancelamento</option>
                </select>
              </div>

              {warning && (
                <div className={`p-4 rounded-lg ${
                  warning.type === 'info' 
                    ? 'bg-blue-50 border border-blue-100' 
                    : 'bg-yellow-50 border border-yellow-100'
                }`}>
                  <div className="flex items-start">
                    {warning.type === 'info' ? (
                      <Info className="text-blue-400 mr-3 mt-0.5" size={20} />
                    ) : (
                      <AlertTriangle className="text-yellow-400 mr-3 mt-0.5" size={20} />
                    )}
                    <div>
                      <h3 className={`text-sm font-medium ${
                        warning.type === 'info' ? 'text-blue-800' : 'text-yellow-800'
                      }`}>
                        {warning.title}
                      </h3>
                      <p className={`mt-1 text-sm ${
                        warning.type === 'info' ? 'text-blue-700' : 'text-yellow-700'
                      }`}>
                        {warning.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detalhes Adicionais
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-32 focus:ring-primary focus:border-primary"
                  placeholder="Descreva os detalhes do seu pedido de cancelamento..."
                />
              </div>

              {error && (
                <div className="text-sm text-red-600">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedReason || !notes.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark disabled:opacity-50"
                >
                  {loading ? 'Enviando...' : 'Enviar Solicitação'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
};

export default CancelOrderModal;